import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import Spinner from '../../components/Spinner';

const AdminCourseListPage = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const { token } = useContext(AuthContext);

    const fetchCourses = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/api/courses`);
            setCourses(res.data);
        } catch (err) {
            console.error("Error fetching courses:", err);
            toast.error("Failed to load courses.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCourses();
    }, []);

    const handleDelete = async (courseId) => {
        if (window.confirm('Are you sure you want to permanently delete this course and all related enrollments? This action cannot be undone.')) {
            const loadingToast = toast.loading('Deleting course...');
            try {
                const config = { headers: { 'x-auth-token': token } };
                await axios.delete(`${API_BASE_URL}/api/courses/${courseId}`, config);
                toast.success('Course successfully deleted!', { id: loadingToast });
                fetchCourses(); // Refresh list
            } catch (err) {
                toast.error('Error occurred while deleting course.', { id: loadingToast });
            }
        }
    };

    if (loading) return <Spinner />;

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="card p-8">
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
                <h1 className="text-3xl font-bold">Course Management</h1>
                <Link to="/admin/add-course" className="btn btn-primary w-full sm:w-auto">
                    + Add New Course
                </Link>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-light-bg dark:bg-dark-bg">
                        <tr>
                            <th className="p-4 font-semibold rounded-tl-lg">Title</th>
                            <th className="p-4 font-semibold">Author</th>
                            <th className="p-4 font-semibold text-right rounded-tr-lg">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {courses.length > 0 ? courses.map((course, index) => (
                            <tr key={course._id} className="border-b border-light-border dark:border-dark-border">
                                <td className="p-4 font-semibold">{course.title}</td>
                                <td className="p-4 text-light-text/70 dark:text-dark-text/70">{course.author || "Not specified"}</td>
                                <td className="p-4 flex gap-2 justify-end">
                                    <Link to={`/admin/edit-course/${course._id}`} className="btn btn-secondary text-sm !py-1 !px-3">
                                        Edit
                                    </Link>
                                    <button onClick={() => handleDelete(course._id)} className="btn btn-danger text-sm !py-1 !px-3">
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="3" className="text-center p-8 text-light-text/60 dark:text-dark-text/60">
                                    No courses created yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </motion.div>
    );
};

export default AdminCourseListPage;